<h2 mat-dialog-title>Create vendor login</h2>

<mat-dialog-content class="dialog-body">
	<p class="text-muted small mb-2">
		<strong>{{ data.row.brand_name }}</strong>
		<span class="text-monospace">({{ data.row.supplier_name }} · {{ data.row.supplier_id }})</span>
	</p>

	<div class="mode-toggle mb-3" *ngIf="canOfferExisting">
		<button type="button" class="mode-btn" [class.mode-active]="!isExistingMode" (click)="setMode('new')">New login</button>
		<button type="button" class="mode-btn" [class.mode-active]="isExistingMode" (click)="setMode('existing')">Link existing login</button>
	</div>

	<form [formGroup]="form" id="vendor-create-login-form" (ngSubmit)="submit()">

		<ng-container *ngIf="isExistingMode">
			<p class="text-muted small mb-2">
				Gives a login that already exists elsewhere access to <strong>{{ data.row.brand_name }}</strong> too — no new username/password.
			</p>

			<div class="cs-field" [class.cs-open]="openMenu === 'existingLogin'" (click)="$event.stopPropagation()">
				<label class="cs-label">Existing login</label>
				<button type="button" class="cs-trigger" [class.cs-placeholder]="!form.get('existingUserid')?.value" (click)="toggleMenu('existingLogin')">
					<span class="cs-value">{{ selectedExistingLoginLabel }}</span>
					<i class="material-icons cs-arrow">arrow_drop_down</i>
				</button>
				<div class="cs-panel" *ngIf="openMenu === 'existingLogin'">
					<input class="cs-search" type="text" [formControl]="existingLoginFilter" placeholder="Search by user id or name"
						(click)="$event.stopPropagation()" autocomplete="off" />
					<button type="button" class="cs-option" *ngFor="let l of filteredExistingLogins"
						[class.cs-selected]="form.get('existingUserid')?.value === l.userid" (click)="selectExistingLogin(l)">
						<span>
							<i class="material-icons cs-box" *ngIf="l.blocked" matTooltip="Blocked">block</i>
							{{ l.userid }} — {{ l.realname }} <span class="login-code">({{ l.salesmancode }})</span>
						</span>
						<i class="material-icons cs-check" *ngIf="form.get('existingUserid')?.value === l.userid">check</i>
					</button>
					<div class="cs-empty" *ngIf="!filteredExistingLogins.length">No logins match.</div>
				</div>
				<div class="cs-hint">This login keeps its own portal default brand — {{ data.row.brand_name }} is added as extra scope.</div>
			</div>
		</ng-container>

		<ng-container *ngIf="!isExistingMode">
			<p *ngIf="!data.usergroups?.length" class="text-danger small mb-2">No user groups were returned. You cannot create a login until groups exist.</p>
			<p *ngIf="!data.locations?.length" class="text-danger small mb-2">No warehouse locations were returned. Check Company Setup / locations.</p>

			<mat-form-field appearance="outline" class="full-width">
				<mat-label>Salesperson code</mat-label>
				<input matInput formControlName="salesman" maxlength="4" autocomplete="off" style="text-transform: uppercase" />
				<mat-hint>Up to 4 characters. A new salesperson record is created and scoped to this brand.</mat-hint>
				<mat-error *ngIf="form.get('salesman')?.hasError('required')">Required</mat-error>
				<mat-error *ngIf="form.get('salesman')?.hasError('pattern')">Letters/numbers, 4 max</mat-error>
			</mat-form-field>

			<mat-form-field appearance="outline" class="full-width">
				<mat-label>User id</mat-label>
				<input matInput formControlName="userid" autocomplete="off" />
				<mat-hint>The vendor's login. Stored lowercase.</mat-hint>
				<mat-error *ngIf="form.get('userid')?.hasError('required')">Required</mat-error>
				<mat-error *ngIf="form.get('userid')?.hasError('pattern')">Letters, numbers, . _ - only</mat-error>
			</mat-form-field>

			<mat-form-field appearance="outline" class="full-width">
				<mat-label>Password</mat-label>
				<input matInput [type]="hidePassword ? 'password' : 'text'" formControlName="password" autocomplete="new-password" />
				<button mat-icon-button matSuffix type="button" (click)="hidePassword = !hidePassword" matTooltip="{{ hidePassword ? 'Show' : 'Hide' }}">
					<mat-icon>{{ hidePassword ? 'visibility_off' : 'visibility' }}</mat-icon>
				</button>
				<button mat-icon-button matSuffix type="button" (click)="generatePassword()" matTooltip="Generate password">
					<mat-icon>vpn_key</mat-icon>
				</button>
				<mat-error *ngIf="form.get('password')?.hasError('required')">Required</mat-error>
				<mat-error *ngIf="form.get('password')?.hasError('minlength')">At least 4 characters</mat-error>
			</mat-form-field>

			<mat-form-field appearance="outline" class="full-width">
				<mat-label>Email</mat-label>
				<input matInput type="email" formControlName="email" placeholder="vendor@example.com" />
			</mat-form-field>

			<div class="cs-field" [class.cs-open]="openMenu === 'group'"
				[class.cs-invalid]="form.get('user_group')?.touched && form.get('user_group')?.invalid"
				(click)="$event.stopPropagation()">
				<label class="cs-label">User group</label>
				<button type="button" class="cs-trigger" [class.cs-placeholder]="!form.get('user_group')?.value" (click)="toggleMenu('group')">
					<span class="cs-value">{{ selectedGroupLabel }}</span>
					<i class="material-icons cs-arrow">arrow_drop_down</i>
				</button>
				<div class="cs-panel" *ngIf="openMenu === 'group'">
					<button type="button" class="cs-option" *ngFor="let g of data.usergroups"
						[class.cs-selected]="isGroupSelected(g)" (click)="selectGroup(g)">
						<span>{{ g.name }}<ng-container *ngIf="g.user_count != null"> ({{ g.user_count }} users)</ng-container></span>
						<i class="material-icons cs-check" *ngIf="isGroupSelected(g)">check</i>
					</button>
				</div>
				<div class="cs-hint">Defaults to the Salesman group.</div>
				<div class="cs-error" *ngIf="form.get('user_group')?.touched && form.get('user_group')?.hasError('required')">Required</div>
			</div>

			<div class="cs-field" *ngIf="hasMultipleBrands" [class.cs-open]="openMenu === 'brands'" (click)="$event.stopPropagation()">
				<label class="cs-label">Brands for this login</label>
				<button type="button" class="cs-trigger" (click)="toggleMenu('brands')">
					<span class="cs-value">{{ selectedBrandLabel }}</span>
					<i class="material-icons cs-arrow">arrow_drop_down</i>
				</button>
				<div class="cs-panel" *ngIf="openMenu === 'brands'">
					<button type="button" class="cs-option" *ngFor="let b of supplierBrands"
						[class.cs-selected]="isBrandSelected(b)" [class.cs-locked]="isBrandLocked(b)" (click)="toggleBrand(b)">
						<span>
							<i class="material-icons cs-box">{{ isBrandSelected(b) ? 'check_box' : 'check_box_outline_blank' }}</i>
							{{ b.brand_name }}<ng-container *ngIf="isBrandLocked(b)"> (default)</ng-container>
						</span>
					</button>
				</div>
				<div class="cs-hint">This supplier carries multiple brands. The first is the portal default; add any others this login should see.</div>
			</div>

			<div class="cs-field" [class.cs-open]="openMenu === 'location'"
				[class.cs-invalid]="form.get('defaultlocation')?.touched && form.get('defaultlocation')?.invalid"
				(click)="$event.stopPropagation()">
				<label class="cs-label">Default location</label>
				<button type="button" class="cs-trigger" [class.cs-placeholder]="!form.get('defaultlocation')?.value" (click)="toggleMenu('location')">
					<span class="cs-value">{{ selectedLocationLabel }}</span>
					<i class="material-icons cs-arrow">arrow_drop_down</i>
				</button>
				<div class="cs-panel" *ngIf="openMenu === 'location'">
					<button type="button" class="cs-option" *ngFor="let loc of data.locations"
						[class.cs-selected]="isLocationSelected(loc)" (click)="selectLocation(loc)">
						<span>{{ loc.loccode }} – {{ loc.locationname || loc.orignalname || loc.loccode }}</span>
						<i class="material-icons cs-check" *ngIf="isLocationSelected(loc)">check</i>
					</button>
				</div>
				<div class="cs-error" *ngIf="form.get('defaultlocation')?.touched && form.get('defaultlocation')?.hasError('required')">Required</div>
			</div>
		</ng-container>
	</form>
</mat-dialog-content>

<mat-dialog-actions align="end">
	<button mat-button type="button" (click)="cancel()">Cancel</button>
	<button
		mat-raised-button
		color="accent"
		type="submit"
		form="vendor-create-login-form"
		[disabled]="saving || !canSubmit || (!isExistingMode && form.invalid)"
	>
		{{ saving ? 'Saving…' : (isExistingMode ? 'Link login' : 'Create login') }}
	</button>
</mat-dialog-actions>
